import { useEffect } from "react";

/** Clear of the ~24px system back-gesture zone on iOS and Android. */
const START_MIN = 36;
/** Past this it stops reading as a "from the left" swipe. */
const START_MAX = 300;
const DISTANCE = 56;
/** Horizontal travel must beat vertical by this much to count. */
const SLOPE = 1.4;
const VERTICAL_ABORT = 40;

/** Sideways-scrolling ancestor, e.g. the wide data tables most pages render. */
function scrollsHorizontally(target: Element | null): boolean {
  let el: Element | null = target;
  while (el && el !== document.body) {
    if (el.scrollWidth > el.clientWidth) {
      const { overflowX } = getComputedStyle(el);
      if (overflowX === "auto" || overflowX === "scroll") return true;
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * Opens the nav on a rightward swipe that starts just inside the left edge —
 * far enough in that the OS back gesture doesn't claim it first.
 *
 * Listeners are passive: the gesture is only ever observed, never used to
 * block a scroll, so it can't introduce touch jank.
 */
export function useSwipeOpen(enabled: boolean, onOpen: () => void) {
  useEffect(() => {
    if (!enabled) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const stop = () => {
      tracking = false;
    };

    const handleStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (touch.clientX < START_MIN || touch.clientX > START_MAX) return;

      const target = event.target as Element | null;
      if (target?.closest(".MuiModal-root")) return;
      if (scrollsHorizontally(target)) return;

      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    };

    const handleMove = (event: TouchEvent) => {
      if (!tracking) return;
      const touch = event.touches[0];
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);

      if (dx < 0 || dy > VERTICAL_ABORT) {
        tracking = false;
        return;
      }
      if (dx > DISTANCE && dx > dy * SLOPE) {
        tracking = false;
        onOpen();
      }
    };

    const opts = { passive: true } as const;
    window.addEventListener("touchstart", handleStart, opts);
    window.addEventListener("touchmove", handleMove, opts);
    window.addEventListener("touchend", stop, opts);
    window.addEventListener("touchcancel", stop, opts);
    return () => {
      window.removeEventListener("touchstart", handleStart);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", stop);
      window.removeEventListener("touchcancel", stop);
    };
  }, [enabled, onOpen]);
}
